import { Injectable } from "@angular/core";
import { API } from "aws-amplify";
import * as moment from "moment";
import { environment } from "src/environments/environment";
import { CONST_AWS_API } from "../constants";
import { CommanSubscribeService } from "./comman-subscribe.service";

@Injectable({ providedIn: "root" })
export class UserActivityService {
  private readonly CATEGORY_MAP: Record<string, string> = {
    network: "NETWORKING",
    event: "EVENT_PLATFORM",
    product: "MARKETPLACE",
    community: "COMMUNITY",
    marketplace: "MARKETPLACE",
    purchase: "PAYMENT",
    nft: "MARKETPLACE",
    "sign-in":  "AUTH",
    "sign-up":  "AUTH",
    "sign-out": "AUTH",
    album: "CONTENT",
    "ai-agent": "AI_AGENT",
    navigation: "NAVIGATION",
  };

  private static readonly GUEST_ID_KEY = "bb_guest_id";
  private static readonly SESSION_ID_KEY = "bb_session_id";

  // Top-level paths that are NOT channel names
  private readonly STATIC_TOP_ROUTES = new Set([
    "user-dashboard",
    "network-market",
    "communities",
    "userhome",
    "signup",
    "stream",
    "watch",
    "event-navigation",
    "merchandise",
    "nft",
    "privacypolicy",
    "cookiepolicy",
    "termsandconditions",
    "contactus",
  ]);

  // Known section keywords that appear as the second (or third) segment in IC URLs
  private readonly IC_SECTIONS = new Set([
    "event",
    "album",
    "album-video",
    "community",
    "merchandise",
    "market",
    "nft",
    "fastChannel",
    "playlist",
    "contactus",
    "signin",
    "sign-up",
    "channelSearch",
    "preview",
    "my-library",
    "subscription-list",
    "privacypolicy",
    "cookiepolicy",
    "termsandconditions",
    "user-dashboard",
    "404",
  ]);

  private geoLocation: Record<string, any> | null = null;

  constructor(private commanSubscribeService: CommanSubscribeService) {}

  requestBrowserGeolocation(): void {
    const cached = sessionStorage.getItem("bb_geo");
    if (cached) {
      try {
        this.geoLocation = JSON.parse(cached);
      } catch {
        /* ignore */
      }
      return;
    }

    // Browser geo — requires user permission
    if (!navigator?.geolocation) return;
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const c = pos.coords;
        // Store every field the browser provides
        this.geoLocation = {
          latitude:         c.latitude,
          longitude:        c.longitude,
          accuracy:         c.accuracy         ?? null,
          altitude:         c.altitude         ?? null,
          altitudeAccuracy: c.altitudeAccuracy  ?? null,
          heading:          c.heading          ?? null,
          speed:            c.speed            ?? null,
        };
        sessionStorage.setItem("bb_geo", JSON.stringify(this.geoLocation));

        // Browser never provides city/country — fetch once via reverse geocoding
        if (!this.geoLocation.city && !this.geoLocation.country) {
          fetch(
            `https://nominatim.openstreetmap.org/reverse?lat=${c.latitude}&lon=${c.longitude}&format=json`,
            { headers: { "Accept-Language": "en" } },
          )
            .then((r) => r.json())
            .then((data: any) => {
              const addr = data?.address || {};
              this.geoLocation = {
                ...this.geoLocation,
                city:        addr.city || addr.town || addr.village || addr.county || null,
                state:       addr.state || null,
                country:     addr.country || null,
                countryCode: addr.country_code?.toUpperCase() || null,
              };
              sessionStorage.setItem("bb_geo", JSON.stringify(this.geoLocation));
            })
            .catch(() => { /* keep browser-only data on failure */ });
        }
      },
      () => { /* permission denied — silently skip */ },
      { timeout: 10000, maximumAge: 60000 },
    );
  }

  track(triggeredInfo: any, duration = 0): void {
    if (!triggeredInfo) return;

    const userInfo = this.commanSubscribeService.userInfo;
    const isGuest = !userInfo?.id;
    const guestId = isGuest ? this.getOrCreateGuestId() : null;
    const sessionId = this.getOrCreateSessionId();
    const geoLocation = this.geoLocation;

    const rawCategory = triggeredInfo.actionType || "";
    const eventCategory =
      this.CATEGORY_MAP[rawCategory] ||
      (rawCategory ? rawCategory.toUpperCase() : null);

    const userName =
      !isGuest && userInfo.user_first_name && userInfo.user_last_name
        ? `${userInfo.user_first_name} ${userInfo.user_last_name}`
        : !isGuest
          ? userInfo?.user_display_name || null
          : null;

    // URL context (channelName, eventSlug, ticketKey, productId, etc.) merged with component metaData
    const urlContext = this.extractUrlContext(window?.location?.pathname || "");
    const merged = { ...urlContext, ...(triggeredInfo.metaData || {}) };
    const eventData = Object.keys(merged).length ? merged : null;

    const body = {
      url: window?.location?.pathname,
      userId: isGuest ? null : userInfo.id,
      guestId,
      userName,
      userEmail: isGuest ? null : userInfo.email || null,
      userPic: isGuest ? null : userInfo.profile_pic || null,
      duration,
      eventName:
        triggeredInfo.clickAction ||
        triggeredInfo.actionName ||
        triggeredInfo.actionType,
      eventCategory: eventCategory || null,
      eventData,
      organizationId:
        triggeredInfo.organizationId ||
        this.commanSubscribeService.orgId ||
        localStorage.getItem("bb_org_id") ||
        null,
      networkGroupId:
        triggeredInfo.networkGroupId ||
        this.commanSubscribeService.activeGroupId ||
        null,
      sessionId,
      geoLocation,
      source: "web",
      clientTimestamp: new Date().toISOString(),
      timeZone:
        !isGuest && userInfo.timezone ? userInfo.timezone : moment.tz.guess(),
      deviceInfo: {
        platform: this.detectPlatform(),
        browser: navigator?.userAgent || null,
      },
    };

    const headers: Record<string, string> = {
      "Content-Type": "application/json",
      "x-api-key": environment.AWS_Cognito.X_Api_Key,
    };
    if (!isGuest && userInfo.access_token) {
      headers["Authorization"] = `Bearer ${userInfo.access_token}`;
    }

    API.post(environment.AWS_Cognito.apiName, CONST_AWS_API.SAVE_USER_ACTIONS, {
      headers,
      response: true,
      body,
    }).catch((err: any) =>
      console.error("[UserActivityService] track failed:", err),
    );
  }

  /**
   * Parses the current pathname into named route parameters so every event
   * automatically carries context like channelName, eventSlug, ticketKey, etc.
   * Component metaData is merged ON TOP of this, so explicit values always win.
   */
  private extractUrlContext(pathname: string): Record<string, any> {
    const seg = pathname.split("/").filter(Boolean);
    const ctx: Record<string, any> = {};
    if (!seg.length) return ctx;

    // /network-market/...
    if (seg[0] === "network-market") {
      switch (seg[1]) {
        case "product-marketplace":
          if (seg[2]) ctx.collectionId = seg[2];
          break;
        case "overviewProduct":
          if (seg[2]) ctx.productId = seg[2];
          if (seg[3]) ctx.collectionId = seg[3];
          break;
        case "product-cart":
        case "product-shipping":
          if (seg[2]) ctx.cartId = seg[2];
          break;
      }
      return ctx;
    }

    // All other top-level static routes — nothing useful to extract
    if (this.STATIC_TOP_ROUTES.has(seg[0])) return ctx;

    // IC channel page: seg[0] = channelName
    ctx.channelName = seg[0];

    // Determine offset: 1 if section is direct, 2 if there is a groupName before it
    let off = 1;
    if (seg[1] && !this.IC_SECTIONS.has(seg[1])) {
      if (seg[2] && this.IC_SECTIONS.has(seg[2])) {
        ctx.groupName = seg[1];
        off = 2;
      } else if (seg.length === 2) {
        // /:channelName/:groupName (channel home with group)
        ctx.groupName = seg[1];
        return ctx;
      }
      // else: /:channelName/:tag_category/:tag_id/:performer_id — just return channelName
      else {
        return ctx;
      }
    }

    const section = seg[off];
    if (!section) return ctx;

    switch (section) {
      case "event":
        this.extractEventContext(seg, off, ctx);
        break;
      case "album":
        if (seg[off + 1]) ctx.tagCategory = seg[off + 1];
        if (seg[off + 2]) ctx.albumSlug = seg[off + 2];
        break;
      case "album-video":
        if (seg[off + 1]) ctx.videoSlug = seg[off + 1];
        break;
      case "playlist":
        if (seg[off + 1]) ctx.playlistId = seg[off + 1];
        break;
      case "community":
        if (seg[off + 1]) ctx.communityChannelId = seg[off + 1];
        break;
      case "merchandise":
        this.extractMerchandiseContext(seg.slice(off + 1), ctx);
        break;
    }

    return ctx;
  }

  private extractEventContext(
    seg: string[],
    off: number,
    ctx: Record<string, any>,
  ): void {
    const next = seg[off + 1];
    if (!next || next === "preview") return;

    const EVENT_KEYWORDS = new Set([
      "home",
      "schedule",
      "expo-list",
      "expo-details",
      "expo-room",
      "private-chat",
      "contacts",
      "networking-list",
      "network-room",
      "network-admin",
      "claim-tickets",
    ]);

    if (!EVENT_KEYWORDS.has(next)) {
      // /:channelName/event/:slug_name  or  /:channelName/event/:slug_name/place-order[/:ticket_key]
      ctx.eventSlug = next;
      if (seg[off + 3]) ctx.ticketKey = seg[off + 3]; // place-order/:ticket_key
      return;
    }

    switch (next) {
      case "home":
        if (seg[off + 2]) ctx.eventSlug = seg[off + 2];
        if (seg[off + 3]) ctx.ticketKey = seg[off + 3];
        break;
      case "schedule":
      case "expo-list":
      case "private-chat":
      case "contacts":
      case "claim-tickets":
        if (seg[off + 2]) ctx.ticketKey = seg[off + 2];
        break;
      case "expo-details":
        if (seg[off + 2]) ctx.expoId = seg[off + 2];
        if (seg[off + 3]) ctx.ticketKey = seg[off + 3];
        break;
      case "expo-room":
        // expo-room/:room_token/:expo_id/:session_id/:ticket_key
        if (seg[off + 2]) ctx.roomToken = seg[off + 2];
        if (seg[off + 3]) ctx.expoId = seg[off + 3];
        if (seg[off + 4]) ctx.sessionId = seg[off + 4];
        if (seg[off + 5]) ctx.ticketKey = seg[off + 5];
        break;
      case "network-room":
      case "network-admin":
        // network-room/:room_id/:session_id/:ticket_key
        if (seg[off + 2]) ctx.roomId = seg[off + 2];
        if (seg[off + 3]) ctx.sessionId = seg[off + 3];
        if (seg[off + 4]) ctx.ticketKey = seg[off + 4];
        break;
      case "networking-list":
        if (seg[off + 2]) ctx.ticketKey = seg[off + 2];
        if (seg[off + 3]) ctx.roomId = seg[off + 3];
        break;
    }
  }

  private extractMerchandiseContext(
    seg: string[],
    ctx: Record<string, any>,
  ): void {
    switch (seg[0]) {
      case "product-marketplace":
        if (seg[1]) ctx.collectionId = seg[1];
        break;
      case "overviewProduct":
        if (seg[1]) ctx.productId = seg[1];
        if (seg[2]) ctx.collectionId = seg[2];
        break;
      case "product-cart":
      case "product-shipping":
        if (seg[1]) ctx.cartId = seg[1];
        break;
    }
  }

  private getOrCreateGuestId(): string {
    let id = localStorage.getItem(UserActivityService.GUEST_ID_KEY);
    if (!id) {
      id = `guest_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      localStorage.setItem(UserActivityService.GUEST_ID_KEY, id);
    }
    return id;
  }

  private getOrCreateSessionId(): string {
    let id = sessionStorage.getItem(UserActivityService.SESSION_ID_KEY);
    if (!id) {
      id = `sess_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      sessionStorage.setItem(UserActivityService.SESSION_ID_KEY, id);
    }
    return id;
  }

  private detectPlatform(): string {
    const ua = navigator?.userAgent || "";
    if (/iPhone|iPad|iPod/i.test(ua)) return "ios";
    if (/Android/i.test(ua)) return "android";
    return "web";
  }
}
